import React,{useState,useEffect} from 'react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer } from 'recharts';
import { BarChart, Bar, XAxis, YAxis, Tooltip } from 'recharts';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { FilterValue,resolveSummaryKey } from './AccessLogSelectors';
import { AccessLogSummary, AccessLogType } from './Inc';
import { getSummaryName } from './ChartSummaryName';

// #region Sample data
interface RadarDataType {
	subject: string;
	value: number;
	fullMark: number;
}

interface AccessLogRadarProps {
	open: boolean;
	itemId: number;// -1は作家　0は全商品　1以降は商品ID
	conditions: FilterValue;
	list: AccessLogType[];
	summaryBank: {[key:string]:string};
	onClose: () => void;
}

// #endregion
const AccessLogRadar: React.FC<AccessLogRadarProps> = (props) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
	const tooltip = !useMediaQuery(theme.breakpoints.down('md'));
	//const [radarData,setRadarData] = useState([] as RadarDataType[]);
	const [states,setStates] = useState({
		tabId:"radar",
		radarData: [] as RadarDataType[],
		isLoading: false,
	});
	const changeTab = (event: React.ChangeEvent<{}>, newValue: string) =>{
		setStates({...states,tabId:newValue});
	}
	const conv = ()=>{
		const radarBank = {} as {[key:string]:RadarDataType};
		for( let data of props.list ){
			if( data.view_type==="I" && props.itemId!==0 && data.item_id!==props.itemId ) continue;
			if( data.view_type==="O" && props.itemId!==-1 ) continue;

			if( props.conditions.filters.items && Object.keys(props.conditions.filters.items).length>0 ){
				const scoreKey = `${data.view_type}_${data.item_id ?? 0}` as string;
				if( !props.conditions.filters.items[scoreKey] ) continue;//フィルタにないものは除外
			}

			const resolved = resolveSummaryKey(data.summary, props.summaryBank, false);
			if (!resolved) continue;
			const { summaryKey } = resolved;

			if( props.conditions.filters.summarys && Object.keys(props.conditions.filters.summarys).length>0 ){
				if( !props.conditions.filters.summarys[summaryKey] ) continue;//フィルタにないものは除外
			}

			if( !radarBank[summaryKey] ){
				radarBank[summaryKey] = {
					subject: getSummaryName(summaryKey),
					value: 0,
					fullMark: 5000,
				};
			}
			const ct = Number(data.count);
			if( ct ){
				radarBank[summaryKey].value += ct;
			}
		}
		return Object.values(radarBank);
	}

	useEffect( ()=>{
		if( props.open ){
			const list = conv();
			setStates({
				...states,
				radarData: list,
				tabId: (list.length<3 || list.length>10) ? "bar" : "radar",
			});
		}
	},[props.open]);

	return (
		<Dialog
			open={props.open}
			onClose={props.onClose}
			scroll="paper"
			aria-labelledby="scroll-dialog-title"
			aria-describedby="scroll-dialog-description"
			maxWidth="md"
			fullWidth
			fullScreen={fullScreen}
		>
			<DialogTitle id="scroll-dialog-title">
				<Tabs
					value={states.tabId}
					onChange={changeTab}
					indicatorColor="primary"
					textColor="primary"
					variant="fullWidth"
					style={{marginBottom:"0em"}}
				>
					<Tab
						value="radar"
						label="レイダーチャート"
						sx={{
							backgroundColor: "#F1F2F3",
							whiteSpace: "nowrap",
							"&.Mui-selected": {
								backgroundColor: "white",
							},
						}}
						disabled={states.radarData.length<3 || states.radarData.length>10}
					/>
					<Tab
						value="bar"
						label="棒グラフ"
						sx={{
							backgroundColor: "#F1F2F3",
							whiteSpace: "nowrap",
							"&.Mui-selected": {
								backgroundColor: "white",
							},
						}}
					/>
				</Tabs>
			</DialogTitle>

			<DialogContent dividers={false} >
				<Box>
					{states.tabId==="radar" ? (
					<ResponsiveContainer width="100%" aspect={fullScreen ? 0.8 : 1.4}>
						<RadarChart
							style={{ width: '100%', fontSize: '0.8rem' }}
							outerRadius="95%"
							data={states.radarData}
							margin={{
								top: 20,
								left: 40,
								right: 40,
								bottom: 20,
							}}
						>
							<PolarGrid />
							<PolarAngleAxis dataKey="subject" />
							<PolarRadiusAxis type="number" domain={[0, 'dataMax']} />
							<Tooltip
								cursor={{
									stroke: 'var(--color-border-2)',
								}}
								contentStyle={{
									backgroundColor: 'white',
									borderColor: '#CCCCCC',
								}}
								active={tooltip ? undefined : false}
							/>
							<Radar name="default" dataKey="value" stroke="#88d884" fill="#88d884" fillOpacity={0.6} />
						</RadarChart>
					</ResponsiveContainer>
					):(
					<ResponsiveContainer width="100%" aspect={fullScreen ? 0.55 : 1.4}>
						<BarChart
							style={{ width: '100%' }}
							layout="vertical"
							data={states.radarData}
							margin={{
								top: 20,
								left: 20,
								right: 0,
								bottom: 20,
							}}
							maxBarSize={40}
							>
							<XAxis type="number" domain={[0, 'dataMax']}/>
							<YAxis dataKey="subject" type="category" fontSizeAdjust={0.45}/>
							<Bar dataKey="value" fill="#88d884" label={{ fill: 'white', position: 'insideRight', textAnchor: 'start' }}/>
						</BarChart>
					</ResponsiveContainer>
					)}
				</Box>
				{Object.keys(props.summaryBank).length>0 &&
				<DialogContentText id="scroll-dialog-description" sx={{ mt: 2, fontSize: '0.8rem' }}>
					{Object.keys(props.summaryBank).map( (key:string,index:number)=>{
						// summary.keyがURLで始まる場合のみ表示
						if( !key.startsWith("URL") ){
							return null;
						}
						return (
							<Box key={index} style={{display:'flex', alignItems:'start', gap:'1rem', margin:0, marginBottom:'0.5rem'}}>
								<p style={{whiteSpace:'nowrap', margin:0}}>
									<span style={{display:'inline-block',width:'1em',height:'1em',backgroundColor:states.radarData.find(x=>x.subject===key)?.value ? "#88d884" : "#CCCCCC",marginRight:'0.5em'}}></span>
									{key}
								</p>
								<p style={{margin:0,wordBreak:'break-all',color:'teal',fontSize:'90%',lineHeight:'1rem'}}>
									{props.summaryBank[key]}
								</p>
							</Box>
						);
					})}
				</DialogContentText>}
			</DialogContent>
			<DialogActions>
				<Button onClick={props.onClose}>閉じる</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AccessLogRadar;